import { useCallback, useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router';
import { Mail, KeyRound, FileDown } from 'lucide-react';
import { supabase } from '@/shared/lib/supabase';
import { downloadJson } from '@/shared/utils/jsonExport';
import {
  UniversalPage,
  UniversalLoadingState,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  useToast,
  type UniversalTabItem,
} from '@sighthost/ui';
import {
  adminCustomerService,
  EdgeFunctionError,
  type AdminCustomerProfile,
  type LifecycleStage,
} from '@/domains/customers/services/adminCustomerService';
import { adminInvoiceService } from '@/domains/billing/services/adminInvoiceService';
import type { CustomerDetailContext } from './detail/customerDetailTypes';
import { formatZARWhole } from '@/shared/utils/format';

const STAGE_TONE: Record<LifecycleStage, StatusTone> = {
  lead: 'info',
  active: 'active',
  suspended: 'overdue',
  cancellation_pending: 'pending',
  churned: 'draft',
};
const STAGE_LABEL: Record<LifecycleStage, string> = {
  lead: 'Lead',
  active: 'Active',
  suspended: 'Suspended',
  cancellation_pending: 'Cancelling',
  churned: 'Churned',
};

interface SummaryNumbers {
  hosting: number;
  unpaidTotal: number;
  /** From `v_admin_customer_summary` — the same view the customers LIST reads.
   *  `null` renders as `—`; it is never coerced to 0. */
  openTickets: number | null;
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [customer, setCustomer] = useState<AdminCustomerProfile | null>(null);
  const [summary, setSummary] = useState<SummaryNumbers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actionPending, setActionPending] = useState(false);

  const refresh = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      adminCustomerService.getById(id),
      adminCustomerService.listLinks(id),
      adminInvoiceService.listForCustomer(id),
    ])
      .then(([profile, links, invoices]) => {
        if (!profile) {
          setError(new Error('Customer not found.'));
          setCustomer(null);
          setSummary(null);
          setLoading(false);
          return;
        }
        const unpaidTotal = invoices
          .filter(
            (i) =>
              i.lifecycle_state === 'unpaid' ||
              i.lifecycle_state === 'grace' ||
              i.lifecycle_state === 'suspended'
          )
          .reduce((s, i) => s + i.total, 0);
        setCustomer(profile);
        setSummary({
          hosting: links.length,
          unpaidTotal,
          openTickets: profile.open_tickets,
        });
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!id) {
    navigate('/admin/customers', { replace: true });
    return null;
  }

  if (loading) {
    return (
      <UniversalPage
        title="Loading customer…"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Customers', href: '/admin/customers' },
          { label: '…' },
        ]}
      >
        <UniversalLoadingState variant="rows" count={4} />
      </UniversalPage>
    );
  }

  if (error || !customer) {
    return (
      <UniversalPage
        title="Customer"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Customers', href: '/admin/customers' },
          { label: 'Not found' },
        ]}
      >
        <NotificationCard
          tone="danger"
          title="Could not load customer"
          body={
            error?.message ?? 'The customer profile could not be loaded.'
          }
        />
        <div>
          <Button variant="outline" onClick={() => navigate('/admin/customers')}>
            Back to customers
          </Button>
        </div>
      </UniversalPage>
    );
  }

  async function resendInvite() {
    if (!customer) return;
    setActionPending(true);
    try {
      const res = await adminCustomerService.resendInvite(customer.customer_id);
      toast({
        tone: res.invite_sent ? 'success' : 'warning',
        title: res.invite_sent ? 'Invite resent' : 'Invite not sent',
        body: res.invite_sent
          ? 'The customer should receive the email within a minute.'
          : 'Edge Function returned invite_sent=false — check email_log.',
      });
    } catch (err) {
      if (err instanceof EdgeFunctionError && err.code === 'customer_already_active') {
        toast({
          tone: 'warning',
          title: 'Account already activated',
          body: 'This customer has already activated their portal account. Click "Reset password" to send them a recovery email instead.',
        });
      } else {
        toast({
          tone: 'danger',
          title: 'Could not resend invite',
          body: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setActionPending(false);
    }
  }

  async function resetPassword() {
    if (!customer) return;
    setActionPending(true);
    try {
      const res = await adminCustomerService.resetPortalPassword(
        customer.customer_id
      );
      toast({
        tone: res.reset_sent ? 'success' : 'warning',
        title: res.reset_sent
          ? 'Password reset sent'
          : 'Reset email not sent',
        body: res.reset_sent
          ? 'The customer should receive the recovery email shortly.'
          : 'Edge Function returned reset_sent=false — check email_log.',
      });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not send reset',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setActionPending(false);
    }
  }

  async function exportData() {
    if (!customer) return;
    setActionPending(true);
    try {
      // W4 (2026-08-11): fulfilment-grade POPIA export. The EF enforces
      // requireFullStaff — a support-tier caller gets a clear 403 here
      // rather than the button being hidden (EF + data layer are the gates).
      const { data, error: fnError } = await supabase.functions.invoke(
        'customer-data-export',
        { body: { customer_id: customer.customer_id } },
      );
      if (fnError) {
        const ctx = (fnError as unknown as { context?: Response }).context;
        let msg = fnError.message;
        if (ctx && typeof ctx.clone === 'function') {
          try {
            const parsed = (await ctx.clone().json()) as { message?: string; code?: string };
            if (parsed.message || parsed.code) msg = parsed.message || parsed.code || msg;
          } catch { /* keep default msg */ }
        }
        throw new Error(msg);
      }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadJson(`popia-export-${customer.customer_id.slice(0, 8)}-${stamp}.json`, data);
      toast({
        tone: 'success',
        title: 'POPIA export downloaded',
        body: 'Handle as confidential — see RUNBOOK_POPIA_DATA_REQUESTS for fulfilment steps.',
      });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not export data',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setActionPending(false);
    }
  }

  const summaryLine = summary
    ? `${summary.hosting} hosting · ${formatZARWhole(summary.unpaidTotal)} unpaid · ${
        summary.openTickets === null ? '—' : summary.openTickets
      } open tickets`
    : '';

  const descriptionLine = [customer.email ?? '—', customer.company]
    .filter((s): s is string => !!s && s.length > 0)
    .join(' · ');

  const tabs: UniversalTabItem[] = [
    { label: 'Overview', to: '.', end: true },
    { label: 'Hosting', to: 'hosting' },
    { label: 'Domains', to: 'domains' },
    { label: 'Billing', to: 'billing' },
    { label: 'Activity', to: 'activity' },
  ];

  const ctx: CustomerDetailContext = { customer, refresh };

  return (
    <UniversalPage
      title={customer.full_name}
      description={descriptionLine}
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Customers', href: '/admin/customers' },
        { label: customer.full_name },
      ]}
      meta={
        <>
          <StatusBadge tone={STAGE_TONE[customer.lifecycle_stage]} dot>
            {STAGE_LABEL[customer.lifecycle_stage]}
          </StatusBadge>
          {summaryLine && <span>{summaryLine}</span>}
        </>
      }
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={resendInvite}
            loading={actionPending}
            disabled={actionPending}
          >
            <Mail className="h-4 w-4" />
            Resend invite
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetPassword}
            loading={actionPending}
            disabled={actionPending}
          >
            <KeyRound className="h-4 w-4" />
            Reset password
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportData}
            loading={actionPending}
            disabled={actionPending}
          >
            <FileDown className="h-4 w-4" />
            Export data (POPIA)
          </Button>
        </>
      }
      tabs={tabs}
      tabsRenderOutlet={false}
    >
      <CustomerDetailOutlet ctx={ctx} />
    </UniversalPage>
  );
}

/**
 * Outlet wrapper that injects the loaded customer profile + refresh callback
 * into each tab via Outlet context. Keeping this in its own component lets
 * the `tabsRenderOutlet={false}` API of UniversalPage hand off rendering to us
 * so we can pass `context`.
 */
function CustomerDetailOutlet({ ctx }: { ctx: CustomerDetailContext }) {
  return <Outlet context={ctx} />;
}
